import React, { useState } from 'react';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import {
	Button,
	IconChevronRightThick,
	IconChevronLeftThick,
} from '@sololearnorg/sol';

import { CourseCategory, ILearningExperience, i18n } from '../../../../symphony';
import { ICategorizedCourse } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { ISlHorizontalScrollCommand, ISlHorizontalScrollState, SlHorizontalScrollList } from '../../../../shared/public/SlHorizontalScrollList/SlHorizontalScrollList';
import { LocalizedLink } from '../../../../shared/public/LocalizedLink/LocalizedLink';
import { CourseItemV3 } from '../../components/CourseItemV3/CourseItemV3';

import s from './CategorizedCourses.scss';

interface ICategorizedCourses {
	categories: ICategorizedCourse[];
	courses: ILearningExperience[];
}

export function CategorizedCourses({
	categories,
	courses,
}: ICategorizedCourses): JSX.Element {
	useStyles(s);
	const [activeCategory, setActiveCategory] = useState<CourseCategory>(CourseCategory.All);
	const [coursesData, setCoursesData] = useState<ILearningExperience[]>(courses);
	const [direction, setDirection] = useState<ISlHorizontalScrollCommand>();
	const [scrollState, setScrollState] = useState<ISlHorizontalScrollState>({
		hasLeftScroll: false,
		hasRightScroll: false,
	});

	const directionHandler = (data: ISlHorizontalScrollCommand): void => {
		setDirection(data);
	};

	const handleCategoryClick = (id: CourseCategory): void => {
		setActiveCategory(id);

		if (id === CourseCategory.All) {
			setCoursesData(courses);
		} else {
			setCoursesData(courses.filter((el) => el.categoryIds.includes(id)));
		}
	};

	return (
		<section className="sl-categorized-courses">
			<div className="sl-categorized-courses__container">
				<h1 className="sl-categorized-courses__title">
					{i18n.t('web-le.course-catalog-title')}
				</h1>

				{categories && (
					<div className="sl-categorized-courses__categories">
						<button
							className={classNames(
								'sl-categorized-courses__arrow',
								'sl-categorized-courses__arrow--previous',
								{ 'sl-categorized-courses__arrow--disable': !scrollState.hasLeftScroll },
							)}
							onClick={() => directionHandler({ direction: 'left' })}
							type="button"
						>
							<IconChevronLeftThick />
						</button>

						<button
							className={classNames(
								'sl-categorized-courses__arrow',
								'sl-categorized-courses__arrow--next',
								{ 'sl-categorized-courses__arrow--disable': !scrollState.hasRightScroll },
							)}
							onClick={() => directionHandler({ direction: 'right' })}
							type="button"
						>
							<IconChevronRightThick />
						</button>

						<SlHorizontalScrollList
							scroll={direction}
							onStateChange={setScrollState}
						>
							{categories.map((el) => (
								<Button
									key={el.id}
									buttonType={
										el.id === activeCategory
											? 'secondary'
											: 'tertiary'
									}
									size="s"
									onClick={() => handleCategoryClick(el.id)}
									className="sl-categorized-courses__btn"
								>
									{i18n.t(el.name)}
								</Button>
							))}
						</SlHorizontalScrollList>
					</div>
				)}

				<div className="sl-categorized-courses__courses">
					{coursesData.length ? (
						coursesData.map((el) => (
							<CourseItemV3 key={el.alias} course={el} />
						))
					) : (
						<div className="sl-categorized-courses__empty-state">
							<p className="sl-categorized-courses__empty-state__text">
								Seems like you’re a Great Sololearner and you
								have added all courses. Wait for more courses to
								be published, but meanwhile, you can explore our
								{' '}
								<LocalizedLink
									to="/codes"
									className="sl-categorized-courses__empty-state__text--link"
								>
									Code Playground
								</LocalizedLink>
								,
								{' '}
								<LocalizedLink
									to="/discuss"
									className="sl-categorized-courses__empty-state__text--link"
								>
									Discuss
								</LocalizedLink>
								{' '}
								and
								{' '}
								<LocalizedLink
									to="/blog"
									className="sl-categorized-courses__empty-state__text--link"
								>
									Blog
								</LocalizedLink>
								{' '}
								sections.
							</p>
							<div className="sl-categorized-courses__empty-state__img">
								<img
									src="https://blob.sololearn.com/web-assets/education-blue-solik.svg"
									alt="solik"
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
